import React from "react";
import { X, Bell, CheckCircle2, Clock, Minus } from "lucide-react";
import { tenantIncidentPipeline } from "../data/execData";

const STAGE_LABELS = ["Incident", "8D", "FMEA", "Ticket"];

const StageDot = ({ state }) => {
  if (state === "done")
    return (
      <div className="flex items-center gap-1.5">
        <CheckCircle2 size={14} color="#27ae60" />
      </div>
    );
  if (state === "in-progress")
    return (
      <div className="flex items-center gap-1.5">
        <Clock size={14} color="#f3b14a" className="animate-pulse" />
      </div>
    );
  return <Minus size={14} color="#636880" />;
};

const stageColor = (s) =>
  s === "done" ? "#27ae60" : s === "in-progress" ? "#f3b14a" : "#636880";

export const TenantDetailPanel = ({ tenant, onClose }) => {
  if (!tenant) return null;
  const c =
    tenant.loopCompletion >= 85 ? "#27ae60" : tenant.loopCompletion >= 50 ? "#f3b14a" : "#E24B4A";
  const incs = tenantIncidentPipeline[tenant.name] || [];

  return (
    <>
      <div
        className="fixed inset-0 z-[400] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
        onClick={onClose}
        data-testid="tenant-detail-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[401] panel-enter flex flex-col"
        style={{
          width: "min(600px, 100vw)",
          background: "#0d0f18",
          borderLeft: "1px solid rgba(255,255,255,0.18)",
        }}
        data-testid="tenant-detail-panel"
      >
        <header className="relative p-5 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex items-center justify-center rounded-md hover:bg-white/5"
            style={{ width: 28, height: 28, border: "1px solid rgba(255,255,255,0.18)", color: "#9198b0" }}
            aria-label="Close tenant detail"
            data-testid="close-tenant-detail-btn"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 22, color: "#f3b14a", fontWeight: 700 }}>
              #{tenant.rank}
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0", letterSpacing: "1px" }}>
              {tenant.type}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 20, color: "#f0f2f8" }}>
              {tenant.name}
            </div>
            <span
              style={{
                background: `${c}1c`,
                border: `1px solid ${c}80`,
                color: c,
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 4,
                fontWeight: 600,
                letterSpacing: "0.8px",
              }}
            >
              {tenant.loopCompletion}% LOOP COMPLETION
            </span>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 pt-4">
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "1.6px",
              color: "#9198b0",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            S1 / S2 Incidents · Quality Loop
          </div>
          {incs.map((inc) => (
            <div
              key={inc.id}
              className="rounded-md mb-3"
              style={{
                background: "#111420",
                border: "1px solid rgba(255,255,255,0.12)",
                padding: 16,
              }}
              data-testid={`tenant-detail-inc-${inc.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9ec3ec" }}>
                  {inc.id}
                </span>
                <span
                  style={{
                    background: inc.severity === "S1" ? "rgba(226,75,74,0.14)" : "rgba(243,156,18,0.13)",
                    border: `1px solid ${inc.severity === "S1" ? "rgba(226,75,74,0.55)" : "rgba(243,156,18,0.5)"}`,
                    color: inc.severity === "S1" ? "#ff7e7c" : "#f3b14a",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 10,
                    padding: "2px 7px",
                    borderRadius: 3,
                    letterSpacing: "1px",
                  }}
                >
                  {inc.severity}
                </span>
              </div>
              <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 14, color: "#f0f2f8" }}>
                {inc.title}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {inc.stages.map((s, i) => (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-1.5">
                      <StageDot state={s} />
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: 11,
                          color: stageColor(s),
                          letterSpacing: "0.3px",
                        }}
                      >
                        {STAGE_LABELS[i]}
                      </span>
                    </div>
                    {i < 3 && (
                      <span
                        style={{
                          width: 18,
                          height: 1,
                          background: s === "done" ? "#27ae60" : "rgba(255,255,255,0.10)",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              {inc.stalledAt && (
                <div
                  className="mt-3 rounded-md flex items-center gap-2"
                  style={{
                    background: "rgba(226,75,74,0.10)",
                    border: "1px solid rgba(226,75,74,0.45)",
                    color: "#ff7e7c",
                    padding: "8px 10px",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 11,
                    letterSpacing: "0.4px",
                  }}
                >
                  Loop stalled at: <strong>{inc.stalledAt}</strong> — {inc.stallDays} days
                </div>
              )}
            </div>
          ))}
          {incs.length === 0 && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#636880" }}>
              No S1/S2 incidents in the last 90 days.
            </div>
          )}
        </div>

        <footer
          className="flex items-center gap-2 p-3"
          style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div className="flex-1" />
          <button
            className="flex items-center gap-1.5 px-3 py-2 rounded-md"
            style={{
              background: "rgba(24,95,165,0.16)",
              border: "1px solid rgba(24,95,165,0.55)",
              color: "#9ec3ec",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.4px",
            }}
            data-testid="tenant-send-reminder-btn"
          >
            <Bell size={12} /> Send reminder
          </button>
        </footer>
      </aside>
    </>
  );
};
